<script setup lang="ts">
import { Heading } from "~/components/clarity-ui/Heading";
import useMainNavigationState from "~/composables/useMainNavigationState";
import usePromoTracking from "~/composables/tracking/usePromoTracking";
import LinkComponent from "~/templates/components/cms/LinkComponent.vue";
import PictureElement from "~/templates/elements/PictureElement.vue";
import { DIMENSIONS } from "~/utils/helper/imaging";
import { containsImage } from "~/utils/helper/magnolia";

export interface ProductTeaserProps {
  heading: string;
  image?: object;
  link: {
    label: string;
    link: string;
    style?: string;
    type: string;
  };
  subline: string;
}

const { heading, image, link, subline } = defineProps<ProductTeaserProps>();

const menuTeaser = ref(null);

const BREAKPOINTS = {
  start: DIMENSIONS["start"].ratio["3/2"],
  md: DIMENSIONS["md"].width["1/2"].ratio["3/2"],
};
const { isMenuOpen } = useMainNavigationState();
const { trackPromoClick, trackViewPromotionIfVisibleAndActive } = usePromoTracking({
  metadata: "",
  creativeName: heading,
  promoName: "menu_teaser",
  promoIndex: 0,
});

onMounted(() => {
  trackViewPromotionIfVisibleAndActive(menuTeaser, isMenuOpen);
});
</script>

<template>
  <div ref="menuTeaser" class="group/teaser relative flex flex-col gap-4 md:max-xl:flex-row">
    <div
      v-if="containsImage(image)"
      class="aspect-3/2 overflow-hidden rounded-2xl md:max-xl:w-6/12"
    >
      <PictureElement
        :img="image"
        :breakpoints="BREAKPOINTS"
        img-class="aspect-3/2 object-cover rounded-2xl motion-safe:transition-transform group-hover/teaser:scale-110"
      />
    </div>
    <div class="flex flex-col items-start justify-center md:max-xl:w-6/12 md:max-xl:pl-4">
      <Heading tag="div" size="medium">{{ heading }}</Heading>
      <div class="mt-2 text-base md:text-xl">{{ subline }}</div>
      <LinkComponent
        v-if="link?.label"
        class="btn-size-small mt-4 inline-flex"
        :stretch-link="true"
        :link="link"
        @click="trackPromoClick"
        >{{ link?.label }}</LinkComponent
      >
    </div>
  </div>
</template>
